import { useEffect, useState } from 'react'

const TOPMOST_DIALOG_CONTAINER = '.MuiDialog-root .MuiDialog-container'

const findTopmostDialogContainer = () => {
  const containers = document.querySelectorAll(TOPMOST_DIALOG_CONTAINER)
  return containers.length > 0 ? containers[containers.length - 1] : null
}

/**
 * Where the floating translator must be rendered so it stays usable while a MUI Dialog is open.
 *
 * An open Dialog does two things to everything else in <body>: its FocusTrap pulls focus back
 * inside, and MUI's ModalManager marks the siblings `aria-hidden`. A widget portaled into <body>
 * is therefore unfocusable and invisible to assistive tech exactly when the PO needs it most —
 * translating what the dialog shows. Rendering the widget INSIDE the topmost dialog's container
 * makes it part of the trapped subtree, so typing, focus and accessibility all work; when the
 * dialog closes the target becomes null and the widget returns to <body>.
 *
 * Dialogs are portaled into <body>, so a childList observer on <body> is enough to notice them
 * opening and closing. Popovers/Menus (e.g. the language dropdowns) are Modals too but are NOT
 * Dialogs; ignoring them avoids re-parenting the panel while one of its own menus is open.
 *
 * @returns {Element|null} the `.MuiDialog-container` of the topmost open dialog, or null
 */
const useDialogPortalTarget = () => {
  const [target, setTarget] = useState(findTopmostDialogContainer)

  useEffect(() => {
    const refresh = () => setTarget(findTopmostDialogContainer())
    refresh()
    const observer = new MutationObserver(refresh)
    observer.observe(document.body, { childList: true })
    return () => observer.disconnect()
  }, [])

  return target
}

export default useDialogPortalTarget
